import Vue from 'vue';
import Component from 'vue-class-component';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import moment from 'moment';
require('focus-visible');

import {
  LoyaltyProfile,
  Promotion,
  CmsLink,
  CustomerProfile,
  DeliveryApiException,
  CustomerDeliveryAddress,
  OrderMealRequestEntree,
  TokenizedPaymentCard,
  PreConfiguredMeal,
  OrderType,
  Restaurant,
  OrderNotFoundException,
  OrderApiException,
  OrderErrorCode,
  MenuSidesAndDrinks
} from '@/models';
import { CountryCode, HeaderOptions } from '@/store/ui';
import { getBrowserProduct, isLoyaltyEnabled, getUrlParams } from '@/helpers';
import { LoyaltyExplainerModalState } from '@/components/modals/CmgLoyaltyExplainerModal/cmg-loyalty-explainer-modal';
import { countries, RESIZE_LISTENER_POLL_RATE_MS, ui } from '@/constants';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { Routes, isGroupOrderRoute } from '@/router';
import { EnrollmentOptions } from '@/services/loyalty/loyalty.service';
import { getCountryByHostname, PersistCrossDomainData } from '@/helpers/country';
import { MUTATIONS as MENU_MUTATIONS } from '@/store/menu';
import { isDesktop } from '@/helpers/util';
import { getCountryCodeByTopLevelDomain } from '@/helpers/country';
import { AccountPageLoadEvent, AddToBagEvent } from '@/services/analytics/events';
import {
  AnalyticsMealType,
  PageLoadEventType,
  RegistrationType
} from '@/services/analytics/platforms/adobe-analytics/models';
import { RestaurantEmbeds } from '@/services/restaurant/restaurant.service';
import { LoginCompleteEvent } from '@/services/analytics/events/login-complete-event';
import {
  RESTAURANT_SELECTED_DECISION_SCOPES,
  USER_PROFILE_DECISION_SCOPES  
} from '@/services/analytics/platforms/adobe-analytics/constants';
import { AemIdComponent, AEMComponents } from '@/models/aem';
import { FormType, MENU_METADATA_CHANNELS } from '@/models';
import { GroupOrderErrorPageType } from '@/views/GroupOrder/GroupOrderError/cmg-group-order-error';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import { AdobeCartAddEvent } from "@/services/analytics/platforms/adobe-analytics/adobe-events";
import { TargetDecisionScope } from '@/models/adobe-target';
import { getEntreeCount } from '@/components/checkout/CmgCheckout/cmg-checkout-helpers';
@Component({
  name: 'cmg-app',
})
export default class CmgApp extends Vue {
  watchers: (() => any)[] = [];
  private isLoadingFavorites: boolean = false;
  private autoLogin = false;
  private isOrderStart = false;
  private cyobModalShownThisLogin = false;
  splitReady: boolean = false;

  get build(): string {
    return this.$services.config.getBuild();
  }

  get scrollable(): boolean {
    return this.$appStore.state.ui.app.scrollable;
  }

  get appIsBlurred(): boolean {
    return this.$appStore.state.ui.app.blurred;
  }

  get contentIsBlurred(): boolean {
    return this.$appStore.state.ui.app.contentBlurred;
  }

  get shouldShowPepper(): boolean {
    return this.$appStore.state.ui.app.pepperVisibility;
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get giftCard(): TokenizedPaymentCard | undefined {
    return this.$appStore.state.customer.wallet.paymentCards.find(card => card.isGiftCard === true);
  }

  get isLoggedIn(): boolean | undefined {
    return this.$services.customer.isLoggedIn();
  }

  get customerLoyaltyProfile(): LoyaltyProfile | null {
    if (!this.$services.loyalty.isLoyaltyEligible(this.customer)) {
      return null;
    }

    return this.$services.loyalty.getLoyaltyProfile(this.customer!);
  }

  get customerPromotions(): Promotion[] {
    return this.$appStore.state.customer.wallet.promotions;
  }

  get headerState(): HeaderOptions {
    return this.$appStore.state.ui.header.options;
  }

  get headerLinks(): CmsLink[] | [] {
    return this.$appStore.state.cms.header.links;
  }

  get containerClasses(): object {
    const browser = this.$services.ui.getBrowserProduct();
    const browserDetectionClassName = `browser-is-${browser.toLowerCase()}`;

    return {
      scrollable: this.scrollable,
      [browserDetectionClassName]: true,
    };
  }

  get isIE(): boolean {
    return getBrowserProduct(navigator) === 'IE';
  }

  get cmgAppContentId(): string {
    return ui.componentIds.cmgAppContent;
  }

  get siteCountryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get sessionFrameUrl(): string {
    return `${this.$services.config.getSessionFrameUrl()}/session.html`;
  }

  get isGroupOrderParticipant(): boolean {
    return this.$services.order.isCustomerParticipantOfGroupOrder();
  }

  get isFullPageLayout(): boolean {
    return this.$router.currentRoute.meta?.isFullPageLayout;
  }

  get isVendorPage(): boolean {
    return [Routes.VendorSignIn, Routes.VendorCreateAccount, Routes.VendorPasswordResetRequestPage].includes(this.$router.currentRoute.name as Routes);
  }

  get showSessionExpiredBanner(): boolean {
    return this.$store.state.ui.showSessionExpiredBanner;
  }

  get AEMComponents(): AemIdComponent[] {
    return AEMComponents;
  }

  get isLoyaltyEligible() {
    return this.$services.loyalty.isLoyaltyEligible(this.customer);
  }

  get isCountryLoyaltyEnabled(): boolean {
    return isLoyaltyEnabled(this.$appStore.state.ui.countryProfile.countryCode);
  }

  get selectedRestaurant(): Restaurant | null {
    return this.$appStore.state.restaurant.selectedRestaurant;
  }

  get isReferredFromReferralProgram(): boolean {
    return !!this.$appStore.state.ui.referrerCustomerIdHash && this.$appStore.state.featureFlags.featureFlags.isRewardsReferralProgramEnabled;
  }

  get isSplitReady(): boolean {
    return !!this.$appStore.state.featureFlags.customerFlagsKey;
  }

  private resizeListener?: () => void;

  async onLoadSessionIframe() {
    const iframe = (document.getElementById('sessionFrame') as unknown) as HTMLIFrameElement;
    if (iframe) {
      const win = iframe.contentWindow;

      window.onmessage = async (event: any) => {
        if (event.origin !== this.$services.config.getSessionFrameUrl()) {
          return;
        }

        if (win) {
          await this.handleCrossDomainDataMessage(event, win);
        }
      };

      // Send a message to the session iframe to return a stored jwt and customer data
      if (win) {
        win.postMessage(JSON.stringify({ key: 'cmg-vuex', method: 'get' }), this.$services.config.getSessionFrameUrl());
      }
    }
  }

  private waitForSplitReady(): Promise<void> {
    return new Promise((resolve) => {
      if (this.isSplitReady) {
        resolve();
        return;
      }

      const unwatch = this.$appStore.watch(
        (state) => state.featureFlags.customerFlagsKey,
        (newValue) => {
          if (newValue) {
            unwatch();
            resolve();
          }
        }
      );
    });
  }

  beforeMount() {
    this.waitForSplitReady().then(() => {
      this.splitReady = true;
    });
  }

  async mounted() {
    // Send Experience Request for Meal Builder page as early as possible to reduce flicker from Adobe Target.
    this.$services.adobeTarget.sendAdobeTargetExperienceRequest(this.$route.name, [TargetDecisionScope.MealBuilderCustomizationsIntegration]);

    const campaignId = this.$route.query.campId?.toString();
    if (campaignId !== undefined) {
      this.$services.ui.setCampaignId(campaignId);
      this.$services.ui.setCampaignChannelId(this.$route.query.campChannelId?.toString());
    }

    this.setTitleAndDescription();
    this.$services.ui.trackClientWidth(document.body.clientWidth);
    this.emitAdobeAnalyticsEventsAfterAdobeLaunchScriptHasLoaded();

    this.$el.parentNode?.insertBefore(document.createComment(`QA Build Reference: ${this.build}`), this.$el);

    this.resetMenuAndMetadataCachesOnce();

    this.autoLogin = !!this.$appStore.state.customer.jwt;

    // If coming from cross-domain and not logged in clear jwt for the current domain
    if (this.$route.query.loggedIn) {
      const loggedIn = this.$route.query.loggedIn == 'true';
      const urlHash = this.getUrlHash();
      this.$router.replace({ query: omit(this.$route.query, 'loggedIn'), hash: urlHash });
      if (!loggedIn) {
        this.$services.customer.setJwt('');
      }
    }

    const upsellItems = this.$services.config.getUpsellItems();
    if (!upsellItems || upsellItems.length === 0) {
      this.$services.config.setUpsellItems();
    }

    const countryProfile = getCountryByHostname(window.location.hostname);
    this.$services.ui.setCountryProfile(countryProfile, false);

    this.watchers.push(this.$appStore.watch(state => state.customer.profile, this.onUserLogin));
    this.watchers.push(this.$appStore.watch(state => state.restaurant.mostRecentRestaurant, this.onFetchMostRecentRestaurant));
    this.watchers.push(this.$appStore.watch(state => state.restaurant.selectedRestaurant, this.onRestaurantSelected));

    this.resizeListener = debounce(() => {
      this.$services.ui.trackClientWidth(document.body.clientWidth);
      if (this.$services.utils.checkDidSwitchDesktopTablet()) {
        this.$services.adobeTarget.sendAdobeTargetExperienceRequest(this.$route.name);
      }
      this.setMetadataCache();

    }, RESIZE_LISTENER_POLL_RATE_MS);

    window.addEventListener('resize', this.resizeListener);

    // expose functions client-side for AEM authors to use
    window.CHIPW = window.CHIPW || {};
    window.CHIPW.ui = window.CHIPW.ui || {};
    window.CHIPW.ui.attachAnalyticsLinkTracking = this.attachAnalyticsLinkTracking;
    window.CHIPW.ui.sendAnalyticsLinkTracking = this.sendAnalyticsLinkTracking;
    window.CHIPW.ui.toggleSignInModal = this.toggleSignInModal;
    window.CHIPW.ui.toggleCreateAccountModal = this.toggleCreateAccountModal;
    window.CHIPW.ui.addPCEToBag = this.addPCEToBag;
    window.CHIPW.ui.makePCEMyOwn = this.makePCEMyOwn;

    this.selectRestaurantByURL();
    this.handleSpecialRoutes();

    // If user refreshes the page or navigates away and back, we must try to poll
    this.$services.order.pollGroupOrderIfExists();

    // update main navigation href to /rewards instead of /order/rewards if user is not logged in
    this.watchers.push(this.$appStore.watch(state => state.customer.jwt, this.handleCustomerRewardsLink, { immediate: true }));
    this.watchers.push(this.$appStore.watch(state => state.customer.profile?.isGuest, this.handleCustomerRewardsLink));
    
    // Watch for CYOB feature flag changes and show modal when enabled
    this.watchers.push(this.$appStore.watch(
      state => state.featureFlags.featureFlags.isChooseYourOwnBirthdayEnabled,
      async (isEnabled) => {
        if (isEnabled) {
          await this.showCyobModal();
        }
      }
    ));

    const restaurant = this.$appStore.state.restaurant.selectedRestaurant;

    if (restaurant && restaurant.address.countryCode && this.$appStore.state.ui.countryProfile.countryCode.toLowerCase() !== restaurant.address.countryCode.toLowerCase()) {
      this.$services.restaurant.selectRestaurant(null, false);
      this.$services.order.setDeliveryAddress(null);
    }

    const scrollElement = document.getElementById(this.cmgAppContentId);
    if (scrollElement) {
      this.$services.googleAnalytics.trackElementScrollPercentage(scrollElement);
    }

    await this.refreshContentFragments();

    // Alert AEM that NOO has finished running.
    window.dispatchEvent(new Event('cmg-app-load'));
  }

  destroyed() {
    for (const unsubFn of this.watchers) {
      unsubFn();
    }

    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  async refreshContentFragments() {
    if (Object.keys(this.$services.config.getAccountModal()).length === 0) {
      this.$services.config.setAccountModal();
    }

    let facModalContent = this.$services.config.getFacModalContent();
    if (!facModalContent) {
      await this.$services.config.setFacModalContent();
      facModalContent = this.$services.config.getFacModalContent();
    }
  }

  private handleCustomerRewardsLink() {
    const authRewards = this.isCountryLoyaltyEnabled ? '/order/rewards' : '/order/offers';
    const unauthRewards = this.isCountryLoyaltyEnabled ? '/rewards' : '/offers';
    let isGuest = (this.customer && this.customer.isGuest) || false;
    let hasJWT = typeof this.$appStore.state.customer.jwt !== 'undefined' && this.$appStore.state.customer.jwt !== '';

    if (!isGuest && hasJWT) {
      document.querySelectorAll('a[href="' + unauthRewards + '"]').forEach(rewardLink => { rewardLink.setAttribute('href', authRewards); });
    } else {
      document.querySelectorAll('a[href="' + authRewards + '"]').forEach(rewardLink => { rewardLink.setAttribute('href', unauthRewards); });
    }
  }

  onSignIn() {
    const args = this.$appStore.state.ui.modals.signIn.args;
    if (args && args.route) {
      this.$router.push({ name: args.route });
    }

    this.$services.ui.setModalVisibility('signIn', false);
  }

  public attachAnalyticsLinkTracking(cta: HTMLAnchorElement) {
    const hasDefinedFields = !!(cta && cta.dataset && (cta.dataset.linkSection || cta.dataset.linkPosition || cta.dataset.linkCategory || cta.dataset.linkTitle));
    const isNotTracked = (cta.dataset && (cta.dataset.isTracking === undefined || cta.dataset.isTracking === 'false'));
    if (hasDefinedFields && isNotTracked) {
      cta.dataset.isTracking = 'true';
      cta.addEventListener('click', (event) => {
        this.$services.analytics.addLinkClickedEventToStore({
          linkClicks: { value: 1 },
          linkSection: cta.dataset?.linkSection,
          linkPosition: cta.dataset?.linkPosition,
          linkCategory: cta.dataset?.linkCategory,
          linkName: cta.innerText?.trim() || '',
          linkTitle: cta.dataset?.linkTitle
        }, cta.href || '');
      });
    }
  }

  public sendAnalyticsLinkTracking(cta: any) {
    const hasDefinedFields = !!(cta && cta.dataset && (cta.dataset.linkSection || cta.dataset.linkPosition || cta.dataset.linkCategory || cta.dataset.linkTitle));
    if (hasDefinedFields) {
      this.$services.analytics.addLinkClickedEventToStore({
        linkClicks: { value: 1 },
        linkSection: cta.dataset?.linkSection,
        linkPosition: cta.dataset?.linkPosition,
        linkCategory: cta.dataset?.linkCategory,
        linkName: cta.innerText?.trim() || '',
        linkTitle: cta.dataset?.linkTitle
      }, cta.href || '');
    }
  }

  public async toggleSignInModal(visible: boolean) {
    //fetch sign-in modal CF if clicked from banner
    if (!this.$services.config.getSignInModal()?.isModalEnabled || Object.keys(this.$services.config.getSignInModal()).length === 0) {
      await this.$services.config.setSignInModal()
      await this.$services.config.setLegalText();
    }
    this.$services.ui.setModalVisibility('signIn', visible);
    this.$services.ui.firstFocus();
  }

  public async toggleCreateAccountModal(showRewardsModal: boolean = false) {
    //fetch create account modal CF if clicked from banner
    if (!this.$services.config.getCreateAccountModal()?.isModalEnabled || Object.keys(this.$services.config.getCreateAccountModal()).length === 0) {
      await this.$services.config.setCreateAccountModal()
    }
    await this.$services.config.setLegalText()

    if (Object.keys(this.$services.config.getAccountOptInToolTips()).length === 0) {
      await this.$services.config.setAccountOptInToolTips()
    }
    await this.$services.ui.setModalVisibility('signIn', true, { type: FormType.CreateAccount });

    if (showRewardsModal) {
      const modalContainer = document.querySelector('.cmg-sign-in-modal .cmg-modal-container');
      if (modalContainer && !modalContainer.classList.contains('modal-rewards')) {
        modalContainer.classList.add('modal-rewards');
      }

      this.$services.ui.setIsFromRewards(true);
    }

    this.$services.ui.firstFocus();
  }

  private getMIMOAnalyticsParams(event: any) {
    const targetElement = event.target as HTMLElement;
    const { dataset } = event.target as HTMLElement;
    const { linkPosition, linkTitle, linkSection, linkCategory } = dataset;
    const params = {
      lname: targetElement?.textContent,
      lposition: linkPosition,
      ltitle: linkTitle,
      lsection: linkSection,
      lcategory: linkCategory
    };
    return getUrlParams(params);
  }

  public async makePCEMyOwn(pceData: PreConfiguredMeal, event: any) {

    await this.findAChipotleIfNeeded();

    // If user closed FAC without selecting a restaurant, do nothing.
    if (!this.selectedRestaurant) {
      return;
    }

    this.$emit('make-it-my-own');
    this.$services.utils.setIsMakeItMyOwn(true);
    // for redirect back to PCE landing page
    this.$services.ui.setPCELandingRoute(this.$route.path, false);

    await this.refreshPCEs();
    await this.$nextTick();

    // If AEM did not send any data, or if entree is unavailable, gracefully prevent order
    const pceEntree = pceData?.entree?.itemId && this.$appStore.state.menu.activeMenu?.entrees?.find(entree => {
      return entree.itemId === pceData.entree.itemId
    });
    if (!pceData || !pceEntree) {
      this.$services.ui.setModalVisibility('basicError', true, {
        header: 'OH DANG.',
        content: 'Sorry, this entree type is unavailable at the current restaurant',
        options: [
          {
            text: 'Okay',
            type: ButtonType.Primary,
            onClick: () => {
              this.$services.ui.setModalVisibility('basicError', false);
            },
          },
        ],
      });
      return;
    }

    // Update prices and isItemAvailable values in pceData
    pceData = this.syncPriceWithLocalStorageMI(pceData);

    // Add AEM PCE data to menu, for Meal Builder page to grab.
    if (!this.$appStore.state.menu.preConfiguredMeals.includes(pceData) && pceEntree.isItemAvailable) {
      this.updateRestaurantPCEs(pceData);
    }
    let analyticsParams = '';
    if (event) {
      analyticsParams = this.getMIMOAnalyticsParams(event);
    }
    let route = `/order/${isGroupOrderRoute(this.$route) ? 'group/' : ''}build/edit/pcm/${pceData.mealId}${analyticsParams}`;
    if (this.hasUnavailableIngredients(pceData)) {
      this.$services.ui.showUnavailableIngredientsModal(pceData, pceData.mealName, () => {
        this.$router.push(route);
        this.$services.bag.setBagVisibility(false);
      });
      this.$services.ui.firstFocus();
    } else {
      this.$services.ui.showMakeItMyOwnModal(pceData.mealName, () => {
        this.$router.push(route);
        this.$services.bag.setBagVisibility(false);
      });
      this.$services.ui.firstFocus();
    }


  }

  public async addPCEToBag(pceData: PreConfiguredMeal, event: any) {

    await this.findAChipotleIfNeeded();

    // If user closed FAC without selecting a restaurant, do nothing.
    if (!this.selectedRestaurant) {
      return;
    }

    // for redirect back to PCE landing page
    this.$services.ui.setPCELandingRoute(this.$route.path, false);

    await this.refreshPCEs();
    await this.$nextTick();

    // If AEM did not send any data, or if entree is unavailable, gracefully prevent order.
    const pceEntree = pceData?.entree?.itemId && this.$appStore.state.menu.activeMenu?.entrees?.find(entree => {
      return entree.itemId === pceData.entree.itemId
    });

    // If we're dealing with a PCE that only has sides or only has drinks (i.e: Single Side of Protein), ensure it can be added to the order if it's available
    let arePCESidesAvailable = false;
    if (!pceEntree && pceData.sides.length > 0) {
      arePCESidesAvailable = pceData.sides.every(pceSide => this.$appStore.state.menu.activeMenu?.sides?.some(menuSide => menuSide.itemId === pceSide.itemId));
    }

    let arePCEDrinksAvailable = false;
    if (!pceEntree && pceData.drinks.length > 0) {
      arePCEDrinksAvailable = pceData.drinks.every(pceDrink => this.$appStore.state.menu.activeMenu?.drinks?.some(menuDrink => menuDrink.itemId === pceDrink.itemId));
    }

    if (!pceData || (!pceEntree && !arePCESidesAvailable && !arePCEDrinksAvailable)) {
      this.$services.ui.setModalVisibility('basicError', true, {
        header: 'OH DANG.',
        content: `Sorry, this ${pceData.sides.length > 0 ? 'side' : 'entree type'} is unavailable at the current restaurant`,
        options: [
          {
            text: 'Okay',
            type: ButtonType.Primary,
            onClick: () => {
              this.$services.ui.setModalVisibility('basicError', false);
            },
          },
        ],
      });
      return;
    }

    // Update prices and isItemAvailable values in pceData
    pceData = this.syncPriceWithLocalStorageMI(pceData);

    // Add AEM PCE data to menu, for Meal Builder page to grab.
    if (!this.$appStore.state.menu.preConfiguredMeals.includes(pceData) && (pceEntree && pceEntree.isItemAvailable || arePCESidesAvailable || arePCEDrinksAvailable)) {
      this.updateRestaurantPCEs(pceData);
    }

    // If ingredients are missing at this restaurant, go to meal builder page and return back to landing page
    if (this.hasUnavailableIngredients(pceData)) {
      this.$services.utils.setIsMakeItMyOwn(true);
      this.$services.ui.showUnavailableIngredientsModal(pceData, pceData.mealName, () => {
        let analyticsParams = '';
        if (event) {
          analyticsParams = this.getMIMOAnalyticsParams(event);
        }
        let route = `/order/${isGroupOrderRoute(this.$route) ? 'group/' : ''}build/edit/pcm/${pceData.mealId}${analyticsParams}`;
        this.$router.push(route);
        this.$services.bag.setBagVisibility(false);
      });
      this.$services.ui.firstFocus();

      // Add PCE to Bag
    } else {
      let preFilledMealName = '';
      if (this.$services.order.isCustomerParticipantOfGroupOrder()) {
        preFilledMealName = this.$appStore.state.order.groupOrderParticpantName;
      } else if (this.$appStore.state.customer.profile && !this.$appStore.state.customer.profile.isGuest) {
        preFilledMealName = this.$appStore.state.customer.profile.firstName;
      }
      this.$emit('add-to-bag');
      this.$services.ui.setModalVisibility('mealBuilderMealName', true, {
        beforeClose: mealName => this.addMealToOrder(mealName, pceData),
        mealName: preFilledMealName,
      });
      this.$services.ui.firstFocus();
    }
  }

  // Send queued analytics events after Adobe Data Layer has become available.
  emitAdobeAnalyticsEventsAfterAdobeLaunchScriptHasLoaded(): void {
    this.$services.adobeTarget.retrieveUtmParameters(this.$route.query);
    const adobeDataLayerInterval = window.setInterval(() => {
      if (window.adobeDataLayer && this.isLoggedIn != undefined) {
        this.$services.analytics.emitQueuedAnalyticsEvents();
        clearInterval(adobeDataLayerInterval);
      }
    }, 500);
  }

  showToastr() {
    this.$services.ui.setToastVisibility(true);
  }

  private hasUnavailableIngredients(pceData: PreConfiguredMeal) {
    return pceData.entree?.contents?.filter(x => !x.isItemAvailable).length > 0 ||
      pceData.sides?.filter(x => !x.isItemAvailable).length > 0 ||
      pceData.drinks?.filter(x => !x.isItemAvailable).length > 0;
  }

  private async findAChipotleIfNeeded() {
    if (!this.$appStore.state.restaurant.selectedRestaurant && !this.$services.order.isCustomerParticipantOfGroupOrder()) {
      await new Promise<void>(resolve => {
        this.$services.ui.setModalVisibility('findAChipotle', true);
        this.$services.ui.setModalVisibility('toast', true, {
          body: 'Before you order, we gotta find your Chipotle first.'.toUpperCase(),
          bodyOnly: true,
        });
        this.$watch('selectedRestaurant', (newVal, oldVal) => {
          if (newVal !== oldVal) {
            const event = new Event("pickupOptionSelected");
            window.dispatchEvent(event);
          }
        });
        window.addEventListener("pickupOptionSelected", () => {
          setTimeout(() => {
            resolve();
          }, 2000)
        })
      });
    }
  }

  private sendAnalyticsEvents(meal: PreConfiguredMeal): void {    
    this.$services.analytics.setAddToBagEventToStore(new AdobeCartAddEvent(new AddToBagEvent(meal, AnalyticsMealType.Standard, this.isOrderStart, undefined, true, 'pce'), this.$appStore, this.$services));
    this.isOrderStart = false; // after a meal is added, always ensure isOrderStart is set to false
  }

  private async addMealToOrder(mealName: string, pceData: PreConfiguredMeal) {
    let order = this.$services.order.getCurrentOrder();
    const isGroupOrder = order && order.order.orderType == OrderType.Group;

    try {
      if (!this.$appStore.state.customer.jwt && !isGroupOrder) {
        await this.$services.customer.createAutogeneratedUser();
      }

      if (!this.$appStore.state.restaurant.selectedRestaurant) {
        const error = 'no restaurant selected even though one must be selected for Whats New to be visible!';
        this.$services.logger.unreachable(error);
        throw new Error(error);
      }

      if (!order) {
        order = await this.$services.order.createOrder(this.$appStore.state.restaurant.selectedRestaurant.restaurantNumber, OrderType.Regular);
        this.isOrderStart = true
      }

      if (!order) {
        const error = 'currentOrder was somehow null after creating a new order!';
        this.$services.logger.unreachable(error);
        throw new Error(error);
      }

      try {
        await this.$services.order.addPreconfiguredMealToOrder(order, pceData.mealId, mealName);

        if (this.$services.order.isCustomerParticipantOfGroupOrder()) {
          this.$router.push({ name: Routes.GroupOrderConfirmation });
        } else {
          // Clear PCE landing route so a subsequent normal meal builder flow is
          // not incorrectly redirected back to the PCE landing page.
          this.$services.ui.setPCELandingRoute('', false);
          this.$services.bag.setBagVisibility(true);
          Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
        }
        this.$services.googleAnalytics.sendCustomEvent({ category: AnalyticsEventCategory.Featured, action: 'addtobag', label: pceData.mealName });
        this.sendAnalyticsEvents(pceData);
      } catch (e: any) {
        this.$services.logger.error('Could not add preconfigured meal to order ' + JSON.stringify(e));

        if (e instanceof OrderNotFoundException && isGroupOrder) {
          this.$services.order.setPendingGroupOrder(null, '');
          this.$router.push({ name: Routes.GroupOrderError, params: { type: GroupOrderErrorPageType.NotFound } });
          return;
        } else if (e instanceof OrderApiException && e.errors.length > 0) {
          const error = e.errors[0];

          if (error) {
            if (isGroupOrder && error.errorCode == OrderErrorCode.OrderCompleted) {
              this.$services.bag.setBagVisibility(false);
              this.$router.push({ name: Routes.GroupOrderError, params: { type: GroupOrderErrorPageType.NotFound } });
              return;
            }

            if (isGroupOrder && error.errorCode == OrderErrorCode.MaxGroupParticipantsExceeded) {
              this.$services.bag.setBagVisibility(false);
              this.$router.push({ name: Routes.GroupOrderError, params: { type: GroupOrderErrorPageType.MaxParticipantCountReached } });
              return;
            }

            if (error.errorCode == OrderErrorCode.MaxEntreeLimitExceeded) {
              this.$services.ui.setModalVisibility('maxEntreeLimitModal', true, { errorMessage: error.errorData, errorCode: error.errorCode });
              return;
            }

            this.$services.ui.setModalVisibility('mealBuilderMealName', false);
            throw e;
          }

          this.$services.logger.error('Something went really wrong when adding item to bag: ', undefined, e);
          this.$services.ui.setModalVisibility('globalErrorToast', true);
        }
      }
    } catch {
      this.$services.ui.setModalVisibility('globalErrorToast', true);
    }
  }

  private setMetadataCache(): void {
    const cachedMetadata = this.$appStore.state.menu.cachedMetadata;

    const desktopMetadata = cachedMetadata[MENU_METADATA_CHANNELS.DESKTOP];
    const mobileMetadata = cachedMetadata[MENU_METADATA_CHANNELS.MOBILE];

    // Get cached metadata and etag based on metadata channel
    const metadata = isDesktop(this.$appStore.state) ? desktopMetadata.data : mobileMetadata.data;
    const etag = isDesktop(this.$appStore.state) ? desktopMetadata.etag : mobileMetadata.etag;

    if (!metadata) {
      this.refreshMenuMetadata(true);

      // Only set metadata from cache if changing from desktop to mobile or vice versa.
    } else if (metadata != this.$appStore.state.menu.metadata) {
      this.$services.metadata.setMenuMetadata(metadata, etag);
    }
  }

  get isRewardsReferralProgramEnabled(): boolean {
    return this.$appStore.state.featureFlags.featureFlags.isRewardsReferralProgramEnabled;
  }

  private async restoreCustomerState() {
    if (!this.$appStore.state.customer.jwt) {
      this.$services.order.resetPendingOrders();
      return;
    }

    try {
      await this.$services.customer.refreshCustomerProfileInfo(false, true);

      if (this.isLoggedIn && this.$route.name === Routes.CreateAccount) {
        await this.$router.push({ name: Routes.EditAccount });
      }

      if (this.isLoggedIn && this.$route.name === Routes.SignIn) {
        await this.$router.push({ name: Routes.EditAccount });
      }

      if (this.isLoggedIn && this.$route.name === Routes.Referrals) {
        await this.handleSignedInReferralLanding(Routes.Home);
      }

      if (this.customer && !this.customer.isGuest) {
        const label = this.$services.loyalty.isEnrolledInLoyalty(this.customer) ? AnalyticsEventLabel.RewardsEnrolled : AnalyticsEventLabel.RewardsNotEnrolled;
        const optOutValue = document.cookie.split('; ').find(row => row.startsWith('opt-out'));
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.Customer,
          action: AnalyticsEventAction.ReAuthenticated,
          label,
          values: { customerEmail: optOutValue ? null : this.customer.email },
        });
      }

      this.popRepromptCountryProfileModal();

      await this.popExplainerModal();
    } catch (e: any) {
      this.$services.logger.error('Something went wrong while re-hydrating user info: ', undefined, e);

      this.$services.order.resetPendingOrders();
    }
  }

  // Route to navigate to after handling a signed-in, non-member visitor.
  // In the referral case (flag on + cref) it's just the backdrop behind the
  // full-screen modal, which handles its own close navigation; otherwise it's
  // where the user actually lands and stays.
  private async handleSignedInReferralLanding(fallbackDestination: Routes): Promise<void> {
    const urlParams = new URLSearchParams(window.location.search);
    this.$services.ui.setReferrerCustomerIdHash(urlParams.get('cref') || '');
    
    if (this.$services.loyalty.isEnrolledInLoyalty(this.customer)) {
      this.$router.push({ name: Routes.Home });
      this.$services.ui.setModalVisibility('alreadyAMemberModal', true);
      return;
    }

    await this.waitForSplitReady();

    if (this.isRewardsReferralProgramEnabled && !!this.$appStore.state.ui.referrerCustomerIdHash) {
      const isPreviousLoyaltyMember = this.$services.loyalty.isEverEnrolledInLoyalty(this.customer);
      this.$services.ui.setModalVisibility(
        isPreviousLoyaltyMember ? 'referralNotEligibleModal' : 'youreInvitedModal', 
        true,
      );
    }

    this.$router.push({ name: fallbackDestination });
  }

  async getPoints() {
    if (!this.customer || !this.isLoyaltyEligible || !this.$services.loyalty.isEnrolledInLoyalty(this.customer)) {
      return;
    }

    try {
      await this.$services.loyalty.refreshPoints();
      await this.$services.reward.getRewardOffers();
    } catch (e) {
      this.$services.logger.error('Something went wrong retrieving rewards points: ', undefined, e);
    }
  }

  private async showCyobModal(): Promise<void> {
    if (this.customer && !this.isVendorPage) {
      const shownCyobModal = await this.$services.ui.showCyobModal(
        this.customer,
        this.$services.reward,
        this.$services.loyalty,
        this.$services.customer,
        this.$appStore
      );

      if (shownCyobModal) {
        this.cyobModalShownThisLogin = true;
      }
    }
  }

  private async popExplainerModal(): Promise<void> {

    // If the customer has not yet selected a country in profile yet, we will pop the explainer modal after forcing customer to choose country if still applicable
    // If the customer is a non-guest who hasn't enrolled in loyalty and they haven't opted out yet and we are in the US country view experience, pop the explainer modal
    // We won't pop modal for Canadians who are not eligible for joining loyalty
    // We won't pop modal during the Chipotle U flow where the customer is referred from the Chipotle U landing page or is viewing the verification modal
    // If the customer is enrolled in loyalty and has not yet seen the rewards general onboarding, show the RewardsGeneralOnboarding modal
    const countryProfile = getCountryByHostname(window.location.hostname);

    if (this.customer && !this.isVendorPage) {
      const { isGuest, country } = this.customer;
      const isEnrolledInLoyalty = this.$services.loyalty.isEnrolledInLoyalty(this.customer);
      const hasNotDeclinedLoyalty = !this.$appStore.state.customer.loyalty.declined;
      const isVerificationModalShown = this.$appStore.state.ui.modals.idMeVerificationModal.visible;
      const isReferredFromChipotleU = localStorage.getItem('referredFromChipotleULandingPage') === 'true';
      const isReferredFromReferralProgram = this.isReferredFromReferralProgram;

      if (!isGuest && !!country && country != 'CA' && countryProfile.countryCode == 'US') {
        // Don't show this if they have just created an account
        if (isEnrolledInLoyalty && !this.customer.hasSeenRewardsGenOnboarding && !this.$appStore.state.customer.newAccount && !isVerificationModalShown) {
            this.$services.ui.showOnboardingModal(this.$services.customer, this.customer);
            await this.$services.ui.waitForModalToClose('loyaltyExplainer');
        } else if (!isEnrolledInLoyalty && hasNotDeclinedLoyalty && !isReferredFromChipotleU && !isReferredFromReferralProgram) {
          if (!this.$services.config.getLoyaltyInterruptModal()) {
            await this.$services.config.setLoyaltyInterruptModal()
          }
          this.$services.ui.setLastSelectedElement('customer-greeting');
          this.$services.ui.setModalVisibility('loyaltyExplainer', true, {
            state: LoyaltyExplainerModalState.WelcomeUnenrolled,
          });
          await this.$services.ui.waitForModalToClose('loyaltyExplainer');
          this.$services.ui.firstFocus();
        } 
      }
    }
  }

  private popRepromptCountryProfileModal(): void {
    if (this.customer && !this.customer.isGuest && !this.customer.country && !this.isVendorPage) {
      this.$services.ui.setModalVisibility('repromptCountryProfileModal', true);
    }
  }

  private popGuacModeModal(): void {
    const path = this.$route.path;
    if (path.includes(`/guacmodeverified`) && (this.$services.config.getGuacMode()?.message || '').toLowerCase() === 'true') {
      this.$services.ui.setModalVisibility('guacModeSignIn', true);
      this.$services.analytics.send(new AccountPageLoadEvent(PageLoadEventType.GuacModeNewAccount));
    }
  }

  private popFAC(): void {
    const facString = 'find-a-chipotle';
    const path = this.$route.path;
    if (path.includes(`/${facString}.html`)) {
      // Maintain support for old code
      // e.g. /find-a-chipotle.html/latLng=33.617871824824256,-117.87175777451856 or /find-a-chipotle.html/query=Irvine, CA 92614, USA
      const searchString = path.split('=')[1];
      this.$services.ui.setModalVisibility('findAChipotle', true, { mode: 'pickup', pickupSearchInput: searchString });
      this.$router.replace({ name: Routes.Order, hash: '#menu' });
    } else if (path.includes(`${facString}`)) {
      // e.g. /order/find-a-chipotle or /find-a-chipotle or /find-a-chipotle?mode=delivery&search=610 Newport Center Dr, Newport Beach, CA 92660
      const queryParams = this.$route.query;
      const modalOptions =
        queryParams.mode == 'delivery'
          ? { mode: 'delivery', deliverySearchInput: queryParams.search as string }
          : { mode: 'pickup', pickupSearchInput: queryParams.search as string };
      this.$services.ui.setModalVisibility('findAChipotle', true, modalOptions);
      // this.$router.replace({ name: Routes.Order, hash: '#menu' });
    }
  }

  private async joinRewards(): Promise<void> {
    const queryParams = this.$route.query;
    if (queryParams.joinRewards == 'true') {
      this.$router.replace({ query: omit(queryParams, 'joinRewards') });
      await this.$services.customer.waitForCustomerInitialization();
      const enrollmentOptions: EnrollmentOptions = { customer: this.customer, pageTitle: document.title };
      await this.$services.loyalty.enrollInLoyalty(enrollmentOptions);
      this.$services.ui.setLastSelectedElement('customer-greeting');
      this.$services.ui.showOnboardingModal(this.$services.customer, this.customer ?? undefined);
    }
  }

  private handleSpecialRoutes(): void {
    this.popFAC();
    this.joinRewards();
    this.popGuacModeModal();
  }

  private async selectRestaurantByURL() {
    const promises: Promise<any>[] = [this.refreshActiveMenu(), this.refreshMenuMetadata(), this.restoreCustomerState(), this.refreshFeatureFlags()];
    let restaurantNumber = parseInt(this.$route.query['restaurant'] as string, 10);
    if(this.$route.query.utm_medium && this.$route.query.utm_medium === 'yext' && this.$route.query.utm_campaign){
      const restaurantMatch = (this.$route.query.utm_campaign as string)?.match(/restaurant=(\d+)/i);
      if (restaurantMatch) {
        restaurantNumber = parseInt(restaurantMatch[1], 10);
      }
    }
    const hideToast = this.$route.query['hideToast'] == 'true';
    const fromFac = this.$route.query['fromFac'] == 'true';
    const urlHash = fromFac ? '#menu' : this.$route.hash;

    if (!isNaN(restaurantNumber) || hideToast || fromFac) {
      this.$router.replace({ query: omit(this.$route.query, ['hideToast', 'restaurant', 'fromFac']), hash: urlHash });
    }

    if (!isNaN(restaurantNumber)) {
      promises.push(this.$services.restaurant.getRestaurantByNumber(restaurantNumber, [
        RestaurantEmbeds.Addresses,
        RestaurantEmbeds.RealHours,
        RestaurantEmbeds.Experience,
        RestaurantEmbeds.Sustainability
      ]));
    }

    const promiseResponses = await Promise.all(promises);

    // Either load the restaurant from query param, or customer's most recently ordered from restaurant (if the restaurant hasn't already been set)
    if (!isNaN(restaurantNumber)) {
      const restaurant = promiseResponses[promiseResponses.length - 1];
      this.$services.order.setDeliveryAddress(null);
      await this.$services.restaurant.selectRestaurant(restaurant, true);

      this.$services.adobeTarget.sendAdobeTargetExperienceRequest(this.$route.name);

      if (!hideToast) {
        this.$services.ui.setModalVisibility('toast', true, {
          title: 'PICKING UP FROM',
          subTitle: (this.$services.customer.getFavoriteRestaurantName(restaurant.restaurantNumber) || '').toUpperCase(),
          body: this.$services.customer.getFavoriteRestaurantName(restaurant.restaurantNumber)
            ? restaurant.address.addressLine1
            : `${restaurant.address.addressLine1}, ${restaurant.address.locality}, ${restaurant.address.administrativeArea} ${(restaurant.address.postalCode || '').split('-')[0]
            }`,
        });
      }
    }
  }

  private async onUserLogin() {
    if (!this.$appStore.state.customer.jwt) {
      return;
    }

    this.$services.adobeTarget.sendAdobeTargetExperienceRequest(undefined, USER_PROFILE_DECISION_SCOPES);
    this.$services.logger.debug('Refreshing data after user login...');

    const hasBag = this.$route.query.hasBag == 'true'; 
    const urlHash = this.getUrlHash();
    if (!hasBag) {
      this.refreshPendingOrders();
    } else {
      this.$router.replace({ query: omit(this.$route.query, 'hasBag'), hash: urlHash });
    }

    this.popRepromptCountryProfileModal();

    await new Promise<void>(resolve => {
      Vue.nextTick(() => {
        setTimeout(async () => {
          await this.popExplainerModal();
          resolve();
        });
      });
    });

    await this.showCyobModal();

    await this.getPoints();

    const onlyProfileOrSubscriptionUpdate = this.$appStore.state.customer.profileSubscriptionChangeOnly ?? false;

    // call refresh most recent order
    if (!this.$appStore.state.order.lastOrder.order && !onlyProfileOrSubscriptionUpdate) {
      this.refreshLastOrder();
    }

    if(localStorage.getItem('referredFromChipotleULandingPage') === 'true') {
      this.$router.replace({ name: Routes.RewardsExchange });
    }

    
    // call refresh payment method, favorites, promos and points only if not a new user
    if (!this.$appStore.state.customer.newAccount) {      
      if (!onlyProfileOrSubscriptionUpdate) {
        this.refreshPaymentMethods();

        this.refreshFavoriteOrders();

        await this.checkForEarnedPointsAndRewards();
      }      
    }

    this.$services.customer.setProfileSubscriptionChangeOnly(false);

    if (this.isLoggedIn) {
      window.dispatchEvent(new Event('cmg-customer-login'));
      this.$services.analytics.send(new LoginCompleteEvent(this.autoLogin ? RegistrationType.Automatic : RegistrationType.Manual));
      this.$services.adobeTarget.sendAdobeTargetExperienceRequest(undefined, [TargetDecisionScope.LoyaltyAutoAccrualFlag]);
    }
  }

  async checkForEarnedPointsAndRewards(): Promise<void> {
    const checkLoyalty = this.$services.loyalty.isLoyaltyEligible(this.$appStore.state.customer.profile) &&
      this.$services.loyalty.isEnrolledInLoyalty(this.$appStore.state.customer.profile);
      
    if(checkLoyalty) {
      const lastUpdated = this.$appStore.state.customer.celebration.lastUpdated;
      if (lastUpdated && moment(lastUpdated).isSame(moment(), 'day')) {
        return;
      }
    
      const promises = await Promise.all([this.refreshCustomerPromotions(), this.$services.loyalty.refreshPoints()]);
      const promos = promises[0];
      const points = promises[1];

      // because of potentially long delays in getting data above, the customer may have moved on to another page
      // that shouldn't actually show celebrations
      if (this.$route.name != Routes.Home && this.$route.name != Routes.Order) {
        return;
      }

      const newRewardCount = promos.length - this.$appStore.state.customer.celebration.numberOfRewards;
      const isCyobModalOpen = this.$appStore.state.ui.modals.cyobRewardModal.visible || this.$appStore.state.ui.modals.cyobRedemptionSuccessModal.visible;
      if(this.cyobModalShownThisLogin || isCyobModalOpen || this.isReferredFromReferralProgram) {
        return;
      } else if (newRewardCount > 0) {
        this.$services.ui.setModalVisibility('rewardsCelebration', true, { newRewardCount });
        this.$nextTick(() => this.$services.ui.focusDiv('.cmg-modal-container'));
      } else if (points && points?.currentPointsBalance > this.$appStore.state.customer.celebration.points) {
        this.$services.ui.setPointsCelebrationVisibility(true);
      }

      if (promos.length && points && points.currentPointsBalance) {
        this.$services.customer.setCelebrationData(promos.length, points.currentPointsBalance);
      }
    }
  }

  private refreshCustomerPromotions() {
    try {
      return this.$services.promotions.refreshCustomerPromotions();
    } catch (e: any) {
      this.$services.logger.error('Something went wrong while fetching customer promotions: ', e);
      return [];
    }
  }

  private async refreshFavoriteOrders(): Promise<any> {
    if (this.isLoadingFavorites) {
      return;
    }
    this.isLoadingFavorites = true;

    try {
      await this.$services.recentAndFavoriteOrders.refreshFavoriteOrders();
    } catch (e: any) {
      this.$services.logger.error('Something went wrong while refreshing favorite orders', undefined, e);
    } finally {
      this.isLoadingFavorites = false;
    }
  }

  private async onRestaurantSelected() {
    this.$services.logger.debug('Refreshing data after restaurant selection...');

    this.$services.adobeTarget.sendAdobeTargetExperienceRequest(undefined, RESTAURANT_SELECTED_DECISION_SCOPES);
    await this.refreshActiveMenu();
    window.dispatchEvent(new Event('cmg-restaurant-selected'));

  }

  private async onFetchMostRecentRestaurant() {
    const mostRecentRestaurant = this.$appStore.state.restaurant.mostRecentRestaurant;
    if (!this.$appStore.state.restaurant.selectedRestaurant && mostRecentRestaurant && mostRecentRestaurant.address.countryCode == this.siteCountryCode) {
      this.$services.restaurant.selectRestaurant(mostRecentRestaurant, this.$services.order.isPickingUp());
    }
  }

  private async refreshMenuMetadata(forceRefresh: boolean = false) {
    return await this.$services.metadata.refreshMenuMetadata(forceRefresh);
  }

  private async refreshActiveMenu() {
    return await this.$services.menu.refreshActiveMenu();
  }

  private async refreshPaymentMethods() {
    await this.$services.transaction.refreshPaymentCards();

    if (this.giftCard) {
      const giftCardBalance = this.$appStore.state.customer.wallet.giftCardBalances[this.giftCard.tokenizedAccountNumber];

      if (!giftCardBalance) {
        await this.$services.transaction.getGiftCardBalance(this.giftCard.tokenizedAccountNumber);
      }
    }
  }

  private async refreshLastOrder() {
    let lastOrder, lastEstimate = null;
    try{
      const restaurantId = await this.$services.order.getLastOrderedRestaurantId();
      const lastOrderId = (await this.$services.order.getRecentOrders(restaurantId, 1)).at(0)?.orderId

      if (lastOrderId) {
        lastOrder = await this.$services.order.getOrder(lastOrderId);
      }

      if (lastOrder?.order.delivery) {
            //if last order is a delivery, we will need a delivery estimate
            const delivery = lastOrder.order.delivery;
        
            lastEstimate = await this.$services.order.getDeliveryEstimate({
              dropOffAddress: {
                addressLine1: delivery.dropOffAddressLine1 || '',
                addressLine2: delivery.dropOffAddressLine2 || '',
                locality: delivery.dropOffAddressCity || '',
                administrativeArea: delivery.dropOffAddressState || '',
                postalCode: delivery.dropOffAddressZipCode || '',
                countryCode: this.$appStore.state.ui.countryProfile.countryCode
              },
              entreeCount: getEntreeCount(lastOrder.order),
              orderValue: lastOrder.order.orderMealsExtendedPrice,
              restaurantId: lastOrder.order.restaurantId,
              orderId: lastOrder.order.orderId
            })
      }
    } catch (e: any) {
      this.$services.logger.error("Something went wrong while refreshing last order", e)
      return;
    }

    if (lastOrder != null) {
      this.$services.order.setLastOrder(lastOrder, lastEstimate);
      //now that we have last order set, we need to refresh order feedback and sustainability metrics
      if (!this.$appStore.state.order.feedback) {
        await this.refreshOrderFeedback(lastOrder.order.orderId)
      }
      if (!this.$appStore.state.customer.sustainabilityMetrics.order) {
        await this.refreshOrderSustainabilityMetrics(lastOrder.order.orderId)
      }
    }
  }

  private async refreshOrderFeedback(orderId: string) {
    try{
      const feedback = await this.$services.feedback.getFeedback(orderId);
      if (feedback) {
        this.$services.feedback.setFeedback(feedback);
      }
    }
    catch (e: any) {
      this.$services.logger.error("Something went wrong while refreshing last order's feedback", e)
    }
  }
  
  private async refreshOrderSustainabilityMetrics(orderId: string) {
    try{
      const orderMetrics = await this.$services.sustainability.getHistorySustainabilityOrder(orderId);
      if (orderMetrics) {
        this.$services.sustainability.setSustainabilityOrderMetrics(orderMetrics);
      }
    } 
    catch (e: any) {
      this.$services.logger.error("Something went wrong while refreshing last order's sustainability metrics")
    }
  }

  private updateRestaurantPCEs(pceData: PreConfiguredMeal) {
    let restaurantPCEs = this.$appStore.state.menu.preConfiguredMeals;
    restaurantPCEs.push(pceData);

    this.$services.menu.setPreConfiguredMeals(restaurantPCEs);
  }

  private async refreshPCEs() {
    const restaurant = this.$appStore.state.restaurant.selectedRestaurant;

    try {
      if (!restaurant) {
        return await this.$services.menu.refreshPreConfiguredMeals(); // Universal PCEs
      } else {
        return await this.$services.menu.refreshPreConfiguredMeals(restaurant.restaurantNumber, true);
      }
    } catch (e: any) {
      this.$services.logger.error('Something went wrong while fetching PreConfiguredMeal data: ', undefined, e);
    }
  }

  private syncPriceWithLocalStorageMI(data: any) {
    const activeMenu = this.$appStore.state.menu.activeMenu;
    if (!activeMenu) {
      console.warn('menu is not defined for restaurant selected');
      return;
    }
    const entreeMI = activeMenu.entrees?.find(entree => entree.itemId == data.entree?.itemId);
    const sideMI = activeMenu.sides?.filter(menuSide => data.sides?.some((pceSide: MenuSidesAndDrinks) => pceSide.itemId == menuSide.itemId));
    const drinkMI = activeMenu.drinks?.filter(menuDrink => data.drinks?.some((pceDrink: MenuSidesAndDrinks) => pceDrink.itemId == menuDrink.itemId));
    if (!entreeMI && sideMI?.length === 0 && drinkMI?.length === 0) {
      console.warn("Entree", data.entree.itemName, "is unavailable at this restaurant");
      console.warn("sideMI", sideMI);
      console.warn("drinkMI", drinkMI);
      data.entree.isItemAvailable = false;
      return data;
    }

    if (data.entree && entreeMI) {
      data.entree.unitPrice = entreeMI.unitPrice;
      data.entree.unitDeliveryPrice = entreeMI.unitDeliveryPrice;
      data.entree.isItemAvailable = entreeMI.isItemAvailable;
    }

    let contentsPrice = 0;
    let drinksPrice = 0;
    let sidesPrice = 0;
    let contentsDeliveryPrice = 0;
    let drinksDeliveryPrice = 0;
    let sidesDeliveryPrice = 0;

    // Sum up all prices from an entree's contents (extra chicken, guac, etc)
    data.entree?.contents?.map((topping: any) => {
      let trueTopping = entreeMI?.contents?.find(toppingMI => topping.itemId === toppingMI.itemId);
      if (trueTopping) {
        topping.unitPrice = trueTopping.unitPrice;
        topping.unitDeliveryPrice = trueTopping.unitDeliveryPrice;
        topping.isItemAvailable = trueTopping.isItemAvailable;
        contentsPrice += trueTopping.unitPrice;
        contentsDeliveryPrice += trueTopping.unitDeliveryPrice;
      } else {
        topping.isItemAvailable = false;
      }
    })

    // Sum up all prices from included drinks
    data.drinks?.map((drink: any) => {
      let trueDrink = activeMenu.drinks?.find(drinkMI => drink.itemId === drinkMI.itemId);
      if (trueDrink) {
        drink.unitPrice = trueDrink.unitPrice;
        drink.unitDeliveryPrice = trueDrink.unitDeliveryPrice;
        drink.isItemAvailable = trueDrink.isItemAvailable;
        drinksPrice += trueDrink.unitPrice;
        drinksDeliveryPrice += trueDrink.unitDeliveryPrice;
      } else {
        drink.isItemAvailable = false;
      }
    })

    // Sum up all prices from included sides
    data.sides?.map((side: any) => {
      let trueSide = activeMenu.sides?.find(sideMI => side.itemId === sideMI.itemId);
      if (trueSide) {
        side.unitPrice = trueSide.unitPrice;
        side.unitDeliveryPrice = trueSide.unitDeliveryPrice;
        side.isItemAvailable = trueSide.isItemAvailable;
        sidesPrice += trueSide.unitPrice;
        sidesDeliveryPrice += trueSide.unitDeliveryPrice;
      } else {
        side.isItemAvailable = false;
      }
    })

    if (data.entree && entreeMI) {
      data.mealPrice = entreeMI.unitPrice + contentsPrice + drinksPrice + sidesPrice;
      data.mealDeliveryPrice = entreeMI.unitDeliveryPrice + contentsDeliveryPrice + drinksDeliveryPrice + sidesDeliveryPrice;
    } else {
      data.mealPrice = contentsPrice + drinksPrice + sidesPrice;
      data.mealDeliveryPrice = contentsDeliveryPrice + drinksDeliveryPrice + sidesDeliveryPrice;
    }

    return data;

  }

  private async refreshPendingOrders() {
    return await this.$services.order.refreshPendingOrders();
  }

  private async refreshFeatureFlags() {
    return await this.$services.featureFlags.refreshApimFeatureFlags();
  }

  private async handleCrossDomainDataMessage(event: any, win: Window) {
    const eventData = event.data && (JSON.parse(event.data) as PersistCrossDomainData);

    await this.handleCrossDomainJwt(eventData, win);

    const promises: Promise<string | void>[] = [];

    if (eventData) {
      if (eventData.deliveryAddress) {
        promises.push(this.handleCrossDomainDeliveryAddress(eventData.deliveryAddress));
      }

      if (eventData.order) {
        const order = await this.$services.order.createOrder(eventData.order.order.restaurantId);

        if (order && eventData.order.order.meals) {
          for (const m of eventData.order.order.meals) {
            if (m.preconfiguredMealId) {
              promises.push(this.$services.order.addPreconfiguredMealToOrder(order, m.preconfiguredMealId, m.mealName));
            } else {
              const entrees = m.entrees as OrderMealRequestEntree[];
              const { mealName, sides, drinks } = m;
              const meal = { mealName, entrees, sides, drinks };
              promises.push(this.$services.order.addMealToOrder(order, { meal }));
            }
          }

          if (eventData.order.order.nonFoodItems) {
            for (const i of eventData.order.order.nonFoodItems) {
              promises.push(this.$services.order.addNonFoodItemToOrder(order, { menuItemId: i.menuItemId, quantity: 1, isUpSell: false }));
            }
          }

          await Promise.all(promises);
          await this.refreshPendingOrders();
        }
      }

      win.postMessage(JSON.stringify({ key: 'cmg-vuex', method: 'set', data: { jwt: eventData.jwt } }), this.$services.config.getSessionFrameUrl());
    }
  }

  private async handleCrossDomainJwt(eventData: PersistCrossDomainData, win: Window) {
    // If logged in and no jwt returned from the session iframe clear out user session
    if (!eventData || !eventData.jwt) {
      // Only update customer in store (with force guest) if TLD is different. This gets triggered on new pageloads as well.
      if (this.customer && this.customer.country != getCountryCodeByTopLevelDomain()) {
        this.$services.customer.setProfile({ ...this.customer, isGuest: true });
      }
      if (win) {
        win.postMessage(JSON.stringify({ key: 'cmg-vuex', method: 'remove' }), this.$services.config.getSessionFrameUrl());
      }
    } else if (eventData && eventData.jwt) {
      if (!this.$appStore.state.customer.jwt) {
        // Persist the jwt returned from the session iframe
        this.$services.customer.setJwt(eventData.jwt);
      }
      if (!this.$appStore.state.customer.profile && !this.$appStore.state.customer.jwt) {
        await this.$services.customer.createAutogeneratedUser();
        await this.$services.customer.refreshCustomerProfileInfo();
      }
      if (!this.$appStore.state.customer.profile) {
        await this.$services.customer.refreshCustomerProfileInfo();
      }
      await this.restoreCustomerState();
    }
  }

  private async handleCrossDomainDeliveryAddress(deliveryAddress: CustomerDeliveryAddress) {
    const validationResult = await this.$services.order.validateDeliveryAddress(this.$services.findAChipotle.customerDeliveryAddressToValidationRequest(deliveryAddress));
    if (validationResult && !(validationResult instanceof DeliveryApiException) && validationResult.status === 200) {
      // If we were able to get a restaurantId we can use that to set the current restaurant
      const restaurantId = validationResult.data.restaurantId;
      if (restaurantId) {
        const restaurant = await this.$services.restaurant.getRestaurantByNumber(restaurantId);
        await this.$services.restaurant.selectRestaurant(restaurant, false);
      } else {
        await this.$services.restaurant.selectRestaurant(null, false);
      }

      // set local selected address before selecting restaurant to ensure we have all the info we need to recalculate things like estimates
      await this.$services.order.setDeliveryAddress(deliveryAddress);
      this.$services.checkout.setDeliveryInstructions(deliveryAddress.notes || '');

      if (this.$route.query.showDeliveryToast == 'true') {
        this.$router.replace({ query: omit(this.$route.query, 'showDeliveryToast') });
        this.showDeliveringToModal(deliveryAddress, restaurantId);
      }

      // Plop phone number and name into store so we can carry over address-specific data to checkout
      this.$services.checkout.setDeliveryPhoneNumber(deliveryAddress.phoneNumber);
      this.$services.checkout.setDeliveryName(deliveryAddress.customerName);
    }
  }

  private showDeliveringToModal(deliveryAddress: CustomerDeliveryAddress, restaurantId: number) {
    this.$services.ui.setModalVisibility('toast', true, {
      title: 'DELIVERING TO',
      subTitle: deliveryAddress.name ? deliveryAddress.name.toUpperCase() : undefined,
      body: deliveryAddress.streetAddress1!,
      legalCopy: 'Menu pricing for delivery is higher and fees apply.',
      icon: this.$appStore.state.cms.images.icons.carWhite.default,
      after: async () => {
        const epops = await this.$services.epop.getEpopsForRestaurant(restaurantId);
        if (epops && epops.length > 0) {
          this.$services.ui.setModalVisibility('epop', true, {
            epops,
          });
          this.$services.ui.firstFocus();
        }
      },
    });
  }



  private getUrlHash() {
    let hash = '';
    if (this.$route.hash) {
      hash = this.$route.hash.indexOf('?') > -1 ? this.$route.hash.substring(0, this.$route.hash.indexOf('?') - 1) : this.$route.hash;
    }
    return hash;
  }

  private resetMenuAndMetadataCachesOnce() {
    const hasResetCache = !!localStorage.getItem('hasResetMenuAndMetadataCaches');

    if (!hasResetCache) {
      localStorage.setItem('hasResetMenuAndMetadataCaches', 'true');

      const cachedMenuRestaurantIds = this.$store.state.menu.cachedMenuRestaurantIds;

      while (cachedMenuRestaurantIds.length) {
        this.$store.commit(MENU_MUTATIONS.REMOVE_CACHED_MENU_FOR_RESTAURANT, cachedMenuRestaurantIds.pop());
      }

      this.$services.metadata.setMenuMetadata();
    }
  }

  private setTitleAndDescription(): void {
    if (this.$appStore.state.cms.home.title) {
      document.title = this.$appStore.state.cms.home.title;
    }
    if (this.$appStore.state.cms.home.description) {
      const descriptionMetaTag = document.createElement('meta');
      descriptionMetaTag.setAttribute('name', 'description');
      descriptionMetaTag.setAttribute('content', this.$appStore.state.cms.home.description);
      document.getElementsByTagName('head')[0].appendChild(descriptionMetaTag);
    }
  }

  get isRewardsExchangeOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsExchangeOnline ?? true;
  }

  get isRewardsOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsOnline ?? true;
  }
}
